import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home,
  Users,
  Wallet,
  Search,
  FileText,
  DollarSign,
  Clock,
  TrendingUp,
  AlertCircle,
  Building,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import { getCurrentProfile, supabase, Account, Flat, Owner, Transaction } from '../lib/supabase';
import toast from 'react-hot-toast';
import { ExportService } from '../services/ExportService';
import { formatDate } from '../lib/dateUtils';

type AccountWithFlat = Account & {
  flats?: Array<{ flat_number: string }>;
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalFlats: 0,
    totalOwners: 0,
    totalOutstanding: 0,
    outstandingAccounts: 0,
    paidAccounts: 0,
    accountsInCredit: 0,
    totalTransactions: 0,
    recentPayments: 0,
    overdueAccounts: 0,
  });
  const [paymentTrend, setPaymentTrend] = useState<any[]>([]);
  const [outstandingByAccount, setOutstandingByAccount] = useState<any[]>([]);
  const [recentTransactions, setRecentTransactions] = useState<any[]>([]);
  const [selectedExport, setSelectedExport] = useState('');
  const [bodyCorporateId, setBodyCorporateId] = useState<string | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const profile = await getCurrentProfile();
      if (!profile?.body_corporate_id) {
        throw new Error('Your user profile is not linked to a body corporate.');
      }

      const currentBodyCorporateId = profile.body_corporate_id;
      setBodyCorporateId(currentBodyCorporateId);

      // Get flats
      const { data: flats } = await supabase
        .from('flats')
        .select('id, status')
        .eq('body_corporate_id', currentBodyCorporateId);

      // Get owners
      const { data: owners } = await supabase
        .from('owners')
        .select('id, is_active')
        .eq('body_corporate_id', currentBodyCorporateId);

      const { data: transactions } = await supabase
        .from('transactions')
        .select(`
          id,
          account_id,
          transaction_date,
          transaction_type,
          debit_amount,
          credit_amount,
          status,
          accounts (account_number, flat_id, flats (flat_number))
        `)
        .eq('body_corporate_id', currentBodyCorporateId)
        .eq('status', 'posted');

      // Calculate account balances
      let totalOutstanding = 0;
      let outstandingAccounts = 0;
      let paidAccounts = 0;
      let accountsInCredit = 0;
      let totalTransactions = 0;
      let recentPayments = 0;

      const accountBalances: { [key: string]: number } = {};

      if (transactions) {
        totalTransactions = transactions.length;
        recentPayments = transactions.filter((t: Transaction) => t.transaction_type === 'payment').length;

        transactions.forEach((t: Transaction) => {
          const amount = (t.debit_amount || 0) - (t.credit_amount || 0);
          accountBalances[t.account_id] = (accountBalances[t.account_id] || 0) + amount;
        });
      }

      // Count account statuses
      Object.values(accountBalances).forEach((balance) => {
        if (balance > 0) {
          totalOutstanding += balance;
          outstandingAccounts++;
        } else if (balance === 0) {
          paidAccounts++;
        } else {
          accountsInCredit++;
        }
      });

      // Get payment trend (last 7 days)
      const trendData = await getPaymentTrend(currentBodyCorporateId);
      setPaymentTrend(trendData);

      // Get top 5 outstanding accounts
      const topOutstanding = await getTopOutstandingAccounts(currentBodyCorporateId);
      setOutstandingByAccount(topOutstanding);

      // Get recent transactions (last 10)
      const recent = await getRecentTransactions(currentBodyCorporateId);
      setRecentTransactions(recent);

      setStats({
        totalFlats: flats?.filter((f: Flat) => f.status === 'occupied').length || 0,
        totalOwners: owners?.filter((o: Owner) => o.is_active).length || 0,
        totalOutstanding,
        outstandingAccounts,
        paidAccounts,
        accountsInCredit,
        totalTransactions,
        recentPayments,
        overdueAccounts: outstandingAccounts, // Accounts with balance > 0 are overdue
      });
    } catch (error) {
      console.error(error);
      toast.error('Error fetching dashboard data');
    }
    setLoading(false);
  };

  const getPaymentTrend = async (currentBodyCorporateId: string) => {
    const dates = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      dates.push(date.toISOString().split('T')[0]);
    }

    const { data } = await supabase
      .from('transactions')
      .select('transaction_date, credit_amount')
      .eq('body_corporate_id', currentBodyCorporateId)
      .eq('transaction_type', 'payment')
      .eq('status', 'posted')
      .gte('transaction_date', dates[0])
      .lte('transaction_date', dates[6]);

    const trend = dates.map((date) => {
      const dailyTotal = data?.filter((t: Transaction) => t.transaction_date === date)
        .reduce((sum: number, t: Transaction) => sum + (t.credit_amount || 0), 0) || 0;
      return {
        date: formatDate(date),
        amount: dailyTotal,
      };
    });

    return trend;
  };

  const getTopOutstandingAccounts = async (currentBodyCorporateId: string) => {
    const { data: accounts } = await supabase
      .from('accounts')
      .select('id, account_number, flat_id, flats (flat_number)')
      .eq('body_corporate_id', currentBodyCorporateId)
      .eq('status', 'active');

    const balances = await Promise.all(
      (accounts || []).map(async (account: AccountWithFlat) => {
        const { data: balance } = await supabase
          .rpc('get_account_balance', { p_account_id: account.id });
        return {
          account_number: account.account_number,
          flat_number: account.flats?.[0]?.flat_number || 'N/A',
          balance: balance || 0,
        };
      })
    );

    return balances
      .filter(b => b.balance > 0)
      .sort((a, b) => b.balance - a.balance)
      .slice(0, 5);
  };

  const getRecentTransactions = async (currentBodyCorporateId: string) => {
    const { data } = await supabase
      .from('transactions')
      .select(`
        *,
        accounts (account_number)
      `)
      .eq('body_corporate_id', currentBodyCorporateId)
      .eq('status', 'posted')
      .order('transaction_date', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(10);

    return data || [];
  };

  const handleExport = async (type: string) => {
    try {
      if (!bodyCorporateId) {
        toast.error('Your body corporate is not configured yet');
        return;
      }

      const exportService = new ExportService(bodyCorporateId);
      let csvContent = '';
      let filename = '';

      switch (type) {
        case 'accounts':
          csvContent = await exportService.exportAccountsToCSV();
          filename = `accounts-${new Date().toISOString().split('T')[0]}.csv`;
          break;
        case 'transactions':
          csvContent = await exportService.exportTransactionsToCSV();
          filename = `transactions-${new Date().toISOString().split('T')[0]}.csv`;
          break;
        case 'audit':
          csvContent = await exportService.exportAuditTrailToCSV();
          filename = `audit-${new Date().toISOString().split('T')[0]}.csv`;
          break;
        default:
          toast.error('Invalid export type');
          return;
      }

      exportService.downloadCSV(csvContent, filename);
      toast.success('Export successful');
    } catch (error) {
      console.error(error);
      toast.error('Error exporting data');
    }
  };

  const quickActions = [
    { label: 'Add Flat', icon: Home, path: '/flats', color: 'bg-green-500' },
    { label: 'Add Owner', icon: Users, path: '/owners', color: 'bg-blue-500' },
    { label: 'Add Levy', icon: DollarSign, path: '/accounts', color: 'bg-purple-500' },
    { label: 'Add Payment', icon: Wallet, path: '/accounts', color: 'bg-orange-500' },
    { label: 'Generate Statement', icon: FileText, path: '/statements', color: 'bg-red-500' },
    { label: 'Search', icon: Search, path: '/accounts', color: 'bg-gray-500' },
  ];

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header with Export */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-gray-500">Welcome back! Here's what's happening with your levy accounts.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedExport}
            onChange={(e) => {
              setSelectedExport(e.target.value);
              if (e.target.value) {
                handleExport(e.target.value);
                setSelectedExport('');
              }
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Export Data</option>
            <option value="accounts">Export Accounts</option>
            <option value="transactions">Export Transactions</option>
            <option value="audit">Export Audit Trail</option>
          </select>
          <button
            onClick={() => navigate('/statements')}
            className="btn-primary flex items-center gap-2"
          >
            <FileText size={18} />
            Statements
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          title="Total Flats"
          value={stats.totalFlats}
          icon={Building}
          color="bg-blue-500"
          subtitle={`${stats.totalFlats} occupied units`}
        />
        <StatCard
          title="Total Owners"
          value={stats.totalOwners}
          icon={Users}
          color="bg-green-500"
          subtitle="Active owners"
        />
        <StatCard
          title="Outstanding Amount"
          value={`R ${stats.totalOutstanding.toLocaleString()}`}
          icon={TrendingUp}
          color="bg-red-500"
          subtitle={`${stats.outstandingAccounts} accounts with balance`}
        />
        <StatCard
          title="Accounts Overview"
          value={stats.outstandingAccounts}
          icon={AlertCircle}
          color="bg-orange-500"
          subtitle={`${stats.paidAccounts} paid, ${stats.accountsInCredit} in credit`}
        />
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Recent Transactions</p>
          <p className="text-2xl font-bold">{stats.totalTransactions}</p>
          <p className="text-xs text-gray-400">Last 30 days</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Recent Payments</p>
          <p className="text-2xl font-bold">{stats.recentPayments}</p>
          <p className="text-xs text-gray-400">Last 30 days</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Overdue Accounts</p>
          <p className="text-2xl font-bold text-red-600">{stats.overdueAccounts}</p>
          <p className="text-xs text-gray-400">Need attention</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Collection Rate</p>
          <p className="text-2xl font-bold text-green-600">
            {stats.totalFlats > 0 
              ? Math.round(((stats.totalFlats - stats.outstandingAccounts) / stats.totalFlats) * 100)
              : 0}%
          </p>
          <p className="text-xs text-gray-400">Accounts paid</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Payment Trend */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <TrendingUp size={18} className="text-gray-400" />
            Payment Trend
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={paymentTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip 
                  formatter={(value: any) => [`R ${value.toFixed(2)}`, 'Payments']}
                />
                <Line 
                  type="monotone" 
                  dataKey="amount" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Outstanding Accounts */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <AlertCircle size={18} className="text-gray-400" />
            Top Outstanding Accounts
          </h3>
          {outstandingByAccount.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No outstanding accounts
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={outstandingByAccount}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="flat_number" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value: any) => [`R ${value.toFixed(2)}`, 'Balance']}
                  />
                  <Bar dataKey="balance" fill="#ef4444">
                    {outstandingByAccount.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <Clock size={18} className="text-gray-400" />
            Recent Transactions
          </h3>
          <button 
            onClick={() => navigate('/transactions')}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            View All
          </button>
        </div>
        {recentTransactions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No recent transactions</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {recentTransactions.map((t) => {
                  const amount = (t.debit_amount || 0) - (t.credit_amount || 0);
                  const isDebit = amount > 0;
                  return (
                    <tr key={t.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm">
                        {formatDate(t.transaction_date)}
                      </td>
                      <td className="px-4 py-2 text-sm font-mono">
                        {t.accounts?.account_number || 'N/A'}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          t.transaction_type === 'payment' ? 'bg-green-100 text-green-800' :
                          ['monthly_levy', 'quarterly_levy', 'special_levy', 'maintenance_levy'].includes(t.transaction_type) ? 'bg-blue-100 text-blue-800' :
                          t.transaction_type === 'penalty' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {t.transaction_type.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm">{t.description}</td>
                      <td className={`px-4 py-2 text-sm text-right font-medium ${
                        isDebit ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {isDebit ? `-R ${Math.abs(amount).toFixed(2)}` : `R ${Math.abs(amount).toFixed(2)}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {quickActions.map((action) => (
            <button
              key={action.label}
              onClick={() => navigate(action.path)}
              className="flex flex-col items-center gap-2 p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-shadow"
            >
              <div className={`p-2 rounded-lg ${action.color}`}>
                <action.icon className="text-white" size={20} />
              </div>
              <span className="text-sm font-medium text-gray-700">{action.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: any;
  color: string;
  subtitle?: string;
}> = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="text-white" size={24} />
      </div>
    </div>
  </div>
);

export default Dashboard;